import {Component, Property} from '@wonderlandengine/api';
import {vec3, quat} from 'gl-matrix';

/**
 * rifle-orient
 */
export class RifleOrient extends Component {
    static TypeName = 'rifle-orient';
    /* Properties that are configurable in the editor */
    static Properties = {
        eye: Property.object(),
        leftController: Property.object(),
        rightController: Property.object(),
    };

    start() {
        // Pre-allocate all math vectors to eliminate GC pauses
        this.posHead = new Float32Array(3);
        this.posLeftCntrl = new Float32Array(3);
        this.posRightCntrl = new Float32Array(3);
        this.lookAtPosition = new Float32Array(3);
        this._upVec = new Float32Array([0, 1, 0]); 

        this.rifleHand = "right"; 
        this._handInitialized = false;

        if (this.engine && this.engine.onXRSessionEnd) {
            this._boundOnXRSessionEnd = this.onXRSessionEnd.bind(this);
            this.engine.onXRSessionEnd.add(this._boundOnXRSessionEnd);
        }
        
        this._boundOnDesktopSessionEnd = this.onDesktopSessionEnd.bind(this);
        window.addEventListener('desktop-session-end', this._boundOnDesktopSessionEnd);
        
        // PERFORMANCE: Throttle hand detection
        this._handCheckTimer = 0;
        const perf = (typeof window !== 'undefined' && window.ARENA_PERF_SETTINGS) ? window.ARENA_PERF_SETTINGS : null;
        this._handCheckInterval = perf && perf.handCheckInterval ? perf.handCheckInterval : 0.15;

        // Desktop mode pre-allocations
        this._desktopOffset = new Float32Array(3);
        this._desktopForward = new Float32Array(3);
        this._desktopLookTarget = new Float32Array(3);
        
        this._lastWeapon = null;
        this._adsOffsets = new Float32Array(3);

        this._nonVrCamera = null;
        if (this.eye && this.eye.parent) {
            const siblings = this.eye.parent.children;
            for (let i = 0; i < siblings.length; i++) {
                if (siblings[i].name === 'NonVrCamera') {
                    this._nonVrCamera = siblings[i];
                    break;
                }
            }
        }

        // Cache initial state for session reset
        this._initialParent = this.object.parent;
        this._initialWorldPos = this.object.getPositionWorld().slice();
        this._initialWorldRot = this.object.getRotationWorld().slice();
    }

    update(dt) {
        if (!(this.engine.xr && this.engine.xr.session)) {
            this._updateDesktop();
            return;
        }

        this._handCheckTimer += dt;
        if (this._handCheckTimer >= this._handCheckInterval) {
            this._handCheckTimer = 0;
            this.setHand(); 
        }
        
        // PERFORMANCE: Object is now parented to the controller. We eliminate 
        // 2 JS-to-WASM calls (getPositionWorld, setPositionWorld) per frame 
        // by letting the engine handle position through the hierarchy.
        if (this.rifleHand === "right") {
            this.leftController.getPositionWorld(this.lookAtPosition);
            this.object.lookAt(this.lookAtPosition, this._upVec);
        } else if (this.rifleHand === "left") {
            this.rightController.getPositionWorld(this.lookAtPosition);
            this.object.lookAt(this.lookAtPosition, this._upVec);
        }
    }

    _updateDesktop() {
        const cam = this._nonVrCamera || this.eye;
        if (!cam) return;

        cam.getPositionWorld(this.posHead);
        cam.getForwardWorld(this._desktopForward);

        const right = this._desktopOffset; 
        cam.getRightWorld(right);

        const b = window._adsBlend || 0;
        const weapon = window._adsWeapon || 'default';

        // PERFORMANCE: Cache string comparisons and offsets
        if (this._lastWeapon !== weapon) {
            this._lastWeapon = weapon;
            if (weapon === 'rifle') {
                this._adsOffsets[0] = 0.3; this._adsOffsets[1] = 0.20; this._adsOffsets[2] = 0.0;
            } else if (weapon === 'shotgun') {
                this._adsOffsets[0] = 0.3; this._adsOffsets[1] = 0.18; this._adsOffsets[2] = 0.0;
            } else if (weapon === 'sniper') {
                this._adsOffsets[0] = 0.35; this._adsOffsets[1] = 0.15; this._adsOffsets[2] = 0.0;
            } else {
                this._adsOffsets[0] = 0.3; this._adsOffsets[1] = 0.225; this._adsOffsets[2] = 0.1;
            }
        }

        const hipFwd = 0.3, hipDown = 0.225, hipRight = 0.1;
        const adsFwd = this._adsOffsets[0], adsDown = this._adsOffsets[1], adsRight = this._adsOffsets[2];

        const fwd = hipFwd + (adsFwd - hipFwd) * b;
        const down = hipDown + (adsDown - hipDown) * b;
        const r = hipRight + (adsRight - hipRight) * b;

        vec3.scaleAndAdd(this._desktopLookTarget, this.posHead, this._desktopForward, fwd);
        this._desktopLookTarget[1] -= down;
        vec3.scaleAndAdd(this._desktopLookTarget, this._desktopLookTarget, right, r);

        this.object.setPositionWorld(this._desktopLookTarget);

        vec3.scaleAndAdd(this._desktopLookTarget, this.posHead, this._desktopForward, 10.0);
        this.object.lookAt(this._desktopLookTarget, this._upVec);
    }

    setHand() {
        this.eye.getPositionWorld(this.posHead);
        this.leftController.getPositionWorld(this.posLeftCntrl);
        this.rightController.getPositionWorld(this.posRightCntrl);

        // PERFORMANCE: Use squared distance to avoid expensive Math.sqrt operations
        const sqrDistLeft = vec3.sqrDist(this.posHead, this.posLeftCntrl);
        const sqrDistRight = vec3.sqrDist(this.posHead, this.posRightCntrl);
        
        const newHand = sqrDistLeft > sqrDistRight ? "right" : "left";

        // PERFORMANCE: Avoid WASM re-parenting overhead if hand hasn't changed
        if (this.rifleHand !== newHand || !this._handInitialized) {
            this.rifleHand = newHand;
            this._handInitialized = true;
            
            this.object.parent = newHand === "right" ? this.rightController : this.leftController;
            
            // By setting local position to zero, we let the native WASM engine 
            // sync the world position every frame for free.
            this.object.setPositionLocal([0, 0, 0]);
        }
    }

    onDesktopSessionEnd() {
        this._resetSession();
        if (this._initialWorldPos && this.object) {
            this.object.setPositionWorld(this._initialWorldPos);
        }
        if (this._initialWorldRot && this.object) {
            this.object.setRotationWorld(this._initialWorldRot);
        }
    }

    onXRSessionEnd() {
        this._resetSession();
        if (this._initialWorldPos && this.object) {
            this.object.setPositionWorld(this._initialWorldPos);
        }
        if (this._initialWorldRot && this.object) {
            this.object.setRotationWorld(this._initialWorldRot);
        }
    }

    _resetSession() {
        this.rifleHand = "right";
        this._handInitialized = false;
        
        // Restore initial parent
        if (this._initialParent !== undefined) {
            this.object.parent = this._initialParent;
        }

        if (this.rightController && this.rightController.getPositionWorld && this.object && this.object.setPositionWorld) {
            this.rightController.getPositionWorld(this.posRightCntrl);
            this.object.setPositionWorld(this.posRightCntrl);
        }
    }

    onDestroy() {
        if (this._boundOnXRSessionEnd && this.engine && this.engine.onXRSessionEnd) {
            this.engine.onXRSessionEnd.remove(this._boundOnXRSessionEnd);
        }
        if (this._boundOnDesktopSessionEnd) {
            window.removeEventListener('desktop-session-end', this._boundOnDesktopSessionEnd);
        }
    }
}
